import { useCreateEvent } from "@/features/events/hooks/useCreateEvent";
import { EventType as NewEventType } from "@/features/events/types/EventType";
import { useGetMealPlans } from "@/features/meal-plan/hooks/useGetMealPlans";
import { useChatNotificationsStore } from "@/utils/stores/useChatNotificationsStore";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { logoutAPI } from "../../../utils/apis/auth-service";
import { saveEvent } from "../../../utils/apis/user-service";
import { config } from "../../../utils/config";
import { Environment } from "../../../utils/enums/Environment";
import { EventType, SourcePage } from "../../../utils/enums/EventType";
import { useAuthStore } from "../../../utils/stores/useAuthStore";
import { useMenuStore } from "../../../utils/stores/useMenuStore";
import { useProductsStore } from "../../../utils/stores/useProductsStore";
import { useUserStore } from "../../../utils/stores/useUserStore";
import { useChat } from "../../chat/hooks/useChat";
import { useGetHealthPlan } from "../../health-plan/hooks/useGetHealthPlan";
import Menu from "../../menu/components/Menu";
import { useIsMetabolicUser, useMetabolicUserType } from "../../metabolic-health/hooks/useMetabolicHealth";
import { MetabolicUserType } from "../../metabolic-health/types/MetabolicUserType";
import { NavBarElement } from "../types/NavBarElement";
import NavBarIcon from "./NavBarIcon";

interface Props {
  currentItem: NavBarElement;
  disabled?: boolean;
}

const NavBar: React.FC<Props> = ({
  currentItem,
  disabled = false,
}) => {
  const { sendEvent } = useCreateEvent()
  const navigate = useNavigate()
  const { auth, logout } = useAuthStore()
  const userId = auth.userId
  const { userData } = useUserStore()
  const email = userData.email
  const { productsData } = useProductsStore()
  const membership = productsData.membership
  const dnaTest = productsData.dnaTest
  const isMetabolicUser = useIsMetabolicUser()
  const { isChatAvailable } = useChat()
  const metabolicUserType = useMetabolicUserType()
  const { unreadSessions } = useChatNotificationsStore();
  const { healthPlans } = useGetHealthPlan();
  const { mealPlans } = useGetMealPlans();

  const metabolicRequirements = useMemo(() => (
    config.environment === Environment.DEVELOP && isMetabolicUser && userId !== "7ee80938-3609-4853-adb9-0d360de69d3d"
  ), [config.environment, isMetabolicUser, userId]);
  const metabolicMapping = () => {
    if (!metabolicUserType) return
    switch (metabolicUserType) {
      case MetabolicUserType.Hook:
        return "Hook"
      case MetabolicUserType.Lite:
        return "Sub lite"
      case MetabolicUserType.Full:
        return "Sub full"
      default:
        return ""
    }
  }
  const dnaTestUser = dnaTest && !membership && config.environment === Environment.DEVELOP

  const { isOpen, openMenu, closeMenu } = useMenuStore();

  const onLogoutClick = () => {
    if (!email) return;
    logoutAPI(
      email,
      async () => {
        closeMenu()
        logout(navigate);
        await sendEvent({
          event: NewEventType.LOGOUT
        })
      },
      (error) => {
        console.log(error);
      },
    );
    email &&
      saveEvent(
        email,
        'logout_button_clicked',
        () => { },
        (error) => {
          console.log(error);
        },
        'menu',
      );
  };

  const handleNavigation = async (
    path: string,
    eventType: string,
    sourcePage: string,
    element: NavBarElement,
  ) => {
    navigate(path);
    // Close mobile menu when navigating
    closeMenu();
    if (currentItem !== eventType && email) {
      saveEvent(
        email,
        eventType,
        () => {},
        () => {},
        sourcePage,
      );
    }
    await sendEvent({
      event: NewEventType.CLICK,
      sub_category: "navbar_element",
      event_metadata: { element }
    })
  };

  const sourcePageMap = {
    [NavBarElement.Home]: SourcePage.HomePage,
    [NavBarElement.HealthPlan]: SourcePage.HealthPlan,
    [NavBarElement.Diagnostica]: SourcePage.Diagnostica,
    [NavBarElement.Equipe]: SourcePage.Equipe,
    [NavBarElement.Menu]: SourcePage.Menu,
    [NavBarElement.Chat]: SourcePage.Chat,
  };

  const handleMenuClick = async () => {
    !isOpen ? openMenu() : closeMenu();
    if (email) {
      const eventType =
        currentItem === NavBarElement.Menu
          ? EventType.MenuCloseNavbarElementClicked
          : EventType.MenuOpenNavbarElementClicked;

      saveEvent(
        email,
        eventType,
        (success) => console.log(success),
        (error) => console.error(error),
        sourcePageMap[currentItem],
      );
      await sendEvent({
        event: NewEventType.CLICK,
        sub_category: "navbar_element",
        event_metadata: { element: NavBarElement.Menu }
      })
    }
    console.log(currentItem);
  };

  const navItems = [
    {
      element: NavBarElement.Home,
      text: 'Home',
      onClick: () =>
        handleNavigation(
          '/home',
          EventType.HomeNavbarElementClicked,
          sourcePageMap[currentItem],
          NavBarElement.Home,
        ),
      icon: <NavBarIcon element={NavBarElement.Home} focused={currentItem === "home" && !isOpen} />,
    },
    ...((healthPlans && healthPlans.length > 0 || mealPlans && mealPlans.length > 0)
      ? [
        {
          element: NavBarElement.HealthPlan,
          text: 'Health Plan',
          onClick: () =>
            handleNavigation(
              '/health-plan',
              EventType.HealthPlanNavbarElementClicked,
              sourcePageMap[currentItem],
              NavBarElement.HealthPlan,
            ),
          icon: <NavBarIcon element={NavBarElement.HealthPlan} focused={currentItem === "health-plan" && !isOpen} />,
        },
      ]
      : []),
    {
      element: NavBarElement.Diagnostica,
      text: 'Diagnostica',
      onClick: () =>
        handleNavigation(
          '/diagnostica',
          EventType.DiagnosticaNavbarElementClicked,
          sourcePageMap[currentItem],
          NavBarElement.Diagnostica,
        ),
      icon: <NavBarIcon element={NavBarElement.Diagnostica} focused={currentItem === "diagnostica" && !isOpen} />,
    },
    {
      element: NavBarElement.Equipe,
      text: 'Equipe',
      onClick: () =>
        handleNavigation(
          '/equipe',
          EventType.EquipeNavbarElementClicked,
          sourcePageMap[currentItem],
          NavBarElement.Equipe,
        ),
      icon: <NavBarIcon element={NavBarElement.Equipe} focused={currentItem === "equipe" && !isOpen} />,
    },
    ...(isChatAvailable
      ? [
        {
          element: NavBarElement.Chat,
          text: 'Chat',
          onClick: () =>
            handleNavigation(
              '/chat',
              EventType.ChatNavbarElementClicked,
              sourcePageMap[currentItem],
              NavBarElement.Chat,
            ),
          icon: <NavBarIcon element={NavBarElement.Chat} focused={currentItem === "chat" && !isOpen} />,
        },
      ]
      : []),
    {
      element: NavBarElement.Menu,
      text: 'Menu',
      onClick: handleMenuClick,
      icon: <NavBarIcon element={NavBarElement.Menu} focused={currentItem === "menu"} menuOpen={isOpen} />,
      className: `${isChatAvailable && "hidden"} md:flex`,
    },
  ];

  // ********************************************************************************

  return (
    <div className={`w-full h-[90px] bg-holifya-navy-blue flex items-center justify-center ${disabled && "opacity-50 pointer-events-none"} md:w-[100px] md:h-full md:flex-col`}>
      <div className="w-[90%] h-full flex items-center justify-between md:flex-col md:h-fit md:space-y-[30px]">
        {
          dnaTestUser && userId !== "17709960-4626-4244-897a-d3107d7f88d7" && <div className="fixed top-0 text-white font-bold text-[10px]">Dna Test User</div>
        }
        {
          metabolicRequirements && userId !== "17709960-4626-4244-897a-d3107d7f88d7" &&
          <div className="fixed top-0 text-white font-bold text-[10px] flex flex-col items-center hidden md:block">
            <div>Metabolic user</div>
            <div>{metabolicMapping()}</div>
          </div>
        }
        {navItems.map(({ element, text, onClick, icon, className }) => (
          <NavBarItem
            key={element}
            icon={icon}
            text={text}
            onClick={onClick}
            classname={className}
            showNotification={element === NavBarElement.Chat && unreadSessions.length > 0}
          />
        ))}
      </div>
      {isOpen && (
        <Menu
          onLogoutClick={onLogoutClick}
        />
      )}
    </div>
  );
};

export default NavBar;

const NavBarItem: React.FC<{
  icon: JSX.Element;
  text: string;
  onClick: () => void;
  classname?: string;
  showNotification?: boolean;
}> = ({ icon, text, onClick, classname = "", showNotification = false }) => {
  return (
    <div
      className={`relative space-y-[5px] h-[50%] flex flex-col items-center justify-between cursor-pointer md:h-fit ${classname}`}
      onClick={onClick}
    >
      <div className="h-[25px]">
        {icon}
        {/* TODO TM */}
        {/* {showNotification && (
          <div className="absolute top-0 right-0 w-2 h-2 bg-holifya-red rounded-full" />
        )} */}
      </div>
      <div className='font-bold text-[10px] leading-[15px] text-white'>
        {text}
      </div>
    </div>
  );
};
